import { useAuth } from "context/AuthContext";
import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";

import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";

import ordersApi from "services/ordersApi";
import { toast } from "react-toastify";
import i18next from "i18next";

export interface MessageData {
  orderId: string;
  fromId: string;
  toId: string;
  message: string;
  time?: string;
  name?: string;
}

interface OrdersApiChatResponse {
  chats: {
    messages: MessageData[];
    orderId: string;
  }[];
}

interface ChatProviderProps {
  children: React.ReactNode;
}

interface ChatContextProps {
  chatData: MessageData[];
  handleSendNewMessage(message: MessageData): void;
}

const ChatContext = createContext<ChatContextProps>({} as ChatContextProps);

export const ChatProvider = ({ children }: ChatProviderProps) => {
  const [chatData, setChatData] = useState<never[] | MessageData[]>([]);

  const isMountedRef = useRef(true);
  const isInitializingRef = useRef(false);
  const hubConnection = useRef<HubConnection | null>(null);
  const shouldForceCloseConnection = useRef(false);

  const { dealerId } = useAuth();

  const handleSendNewMessage = useCallback((message: MessageData) => {
    hubConnection.current?.invoke("SendMessage", message);
  }, []);

  const handleGetInProgressChat = useCallback(async () => {
    if (!dealerId) return;

    const chatInitialData = await ordersApi.get<OrdersApiChatResponse>(
      `v1/dealers/${dealerId}/orders/chats`,
    );

    const allChats: MessageData[] = [];

    chatInitialData.data.chats.forEach((chat) => {
      chat.messages.forEach((message) => allChats.push(message));
    });

    setChatData(allChats);
  }, [dealerId]);

  const closeConnection = useCallback(async () => {
    if (
      hubConnection.current &&
      hubConnection.current.state === HubConnectionState.Connected
    ) {
      try {
        console.log("fechando conexão do chat");

        hubConnection.current.off("MessageReceived");

        await hubConnection.current.stop();
      } catch (error) {
        console.error("error ao fechar a conexão", error);
      } finally {
        hubConnection.current = null;
      }
    }
  }, []);

  const initializeConnection = useCallback(async () => {
    if (isInitializingRef.current || !isMountedRef.current || !dealerId) {
      return;
    }

    console.log("Inicializando conexão do chat");

    isInitializingRef.current = true;

    try {
      await closeConnection();

      hubConnection.current = new HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_ORDERS_API_URL}order-hubs/chat`, {
          withCredentials: false,
        })
        .configureLogging(
          process.env.NODE_ENV === "development"
            ? LogLevel.Information
            : LogLevel.Error,
        )
        .withAutomaticReconnect([
          0, 3000, 5000, 10000, 15000, 30000, 60000, 120000,
        ])
        .build();

      hubConnection.current.onreconnected(async () => {
        console.log("conexão restabelecida do chat");

        if (
          isMountedRef.current &&
          hubConnection.current &&
          hubConnection.current.state === HubConnectionState.Connected
        ) {
          await hubConnection.current.invoke("JoinChat", dealerId);
          console.log("Obtendo mensagens via API...");
          await handleGetInProgressChat();
        }
      });

      hubConnection.current.onclose(async () => {
        console.log("conexão fechada do chat");

        if (isMountedRef.current && !shouldForceCloseConnection.current) {
          await initializeConnection();
        }
      });

      hubConnection.current.on("MessageReceived", (data) => {
        setChatData((prevData) => [...prevData, data]);
        if (data.fromId !== dealerId) {
          toast(i18next.t("newMessagesInChat").toString(), {
            type: "info",
          });
        }
      });

      await hubConnection.current.start();
      console.log("conexão do chat iniciada");

      if (
        isMountedRef.current &&
        !shouldForceCloseConnection.current &&
        hubConnection.current &&
        hubConnection.current.state === HubConnectionState.Connected
      ) {
        await hubConnection.current.invoke("JoinChat", dealerId);
        console.log("Obtendo mensagens via API...");
        await handleGetInProgressChat();
      }
    } catch (error) {
      console.error("error ao iniciar o SignalR:", error);

      const retryDelay = Math.min(5000 * 2 ** Math.random(), 30000);

      setTimeout(() => {
        if (isMountedRef.current && !shouldForceCloseConnection.current) {
          initializeConnection();
        }
      }, retryDelay);

      return;
    } finally {
      isInitializingRef.current = false;
    }
  }, [dealerId, closeConnection, handleGetInProgressChat]);

  useEffect(() => {
    if (shouldForceCloseConnection.current) return () => {};

    isMountedRef.current = true;
    initializeConnection();

    return () => {
      isMountedRef.current = false;
      closeConnection();
    };
  }, [dealerId, initializeConnection, closeConnection]);

  useEffect(() => {
    const checkAndReset = () => {
      if (shouldForceCloseConnection.current) {
        const resetTimer = setTimeout(() => {
          shouldForceCloseConnection.current = false;
        }, 5000);

        return () => {
          clearTimeout(resetTimer);
        };
      }
      return () => {};
    };

    return checkAndReset();
  }, []);

  useEffect(() => {
    const handleRestoreConnection = () => {
      shouldForceCloseConnection.current = false;

      if (
        hubConnection.current &&
        hubConnection.current.state === HubConnectionState.Connected
      ) {
        return;
      }

      console.log("Executando conexão do chat...");
      initializeConnection();
    };

    const handleCloseConnection = () => {
      if (
        hubConnection.current &&
        hubConnection.current.state === HubConnectionState.Connected
      ) {
        shouldForceCloseConnection.current = true;
        closeConnection();
      }
    };

    window.addEventListener(
      "RESTORE_CHAT_CONNECTION",
      handleRestoreConnection as EventListener,
    );

    window.addEventListener(
      "CLOSE_CHAT_CONNECTION",
      handleCloseConnection as EventListener,
    );

    return () => {
      window.removeEventListener(
        "RESTORE_CHAT_CONNECTION",
        handleRestoreConnection as EventListener,
      );
      window.removeEventListener(
        "CLOSE_CHAT_CONNECTION",
        handleCloseConnection as EventListener,
      );
    };
  }, [initializeConnection, closeConnection]);

  return (
    <ChatContext.Provider
      value={{
        chatData,
        handleSendNewMessage,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export function useChat(): ChatContextProps {
  const context = useContext(ChatContext);

  if (!context) {
    throw new Error("useChat must be used within a ChatContext");
  }
  return context;
}
